'use client';

import { useState, type ComponentPropsWithRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { cn } from '@/lib/utils';
import { NotifyMeTrigger } from '@/features/notify-me';
import { useProduct } from '@/providers/product-context';
import { Button, MatchesProgress, OptionChip, StarRating, Swatch } from '@/shared-ui';
import { AddToBagIcon, PlusSwatchIcon } from '@/shared-ui/icons';
import {
    getProductCardActionState,
    getProductCardAttribute,
    type ProductCardActionKind,
    type ProductCardActionState,
} from './lib/product-card-helpers';
import { useVisibleSwatchCount } from './product-card-swatch-layout';

// Action — onClick handler keeps this client-only.

type ProductCardActionClickHandler = (
    state: ProductCardActionState,
    product: ShopperProducts.schemas['Product']
) => void;

interface ActionProps extends ComponentPropsWithRef<'div'> {
    /**
     * Force a specific action kind, overriding the inventory-derived default (orderable → add-to-bag;
     * out of stock → notify-me or out-of-stock). Useful for tiles that should always nudge shade/size.
     */
    kind?: ProductCardActionKind;
    /** Clicked-CTA handler; receives the resolved state + product. Ignored when children are supplied. */
    onActionClick?: ProductCardActionClickHandler;
}

export const Action = ({ children, kind, onActionClick, className, ref, ...props }: ActionProps) => {
    const product = useProduct();
    const hasOverride = children !== undefined && children !== null && children !== false;
    if (!hasOverride && !product) return null;
    return (
        <div
            ref={ref}
            data-slot="product-card-action"
            className={cn(
                'col-start-1 row-start-2 block w-full min-w-0 px-4 pb-2',
                // Desktop: image-bottom overlay revealed on hover/focus-within (focus-within keeps it
                // keyboard/SR-reachable — WCAG 2.1.1).
                'md:row-start-1 md:z-10 md:self-end md:justify-self-stretch md:px-4 md:pb-2 md:pt-0',
                'md:hidden md:group-hover/product-card:block md:group-focus-within/product-card:block',
                className
            )}
            {...props}>
            {hasOverride ? (
                children
            ) : product ? (
                <ActionAutoButton product={product} kind={kind} onActionClick={onActionClick} />
            ) : /* v8 ignore next -- unreachable: early return fires when !hasOverride && !product */ null}
        </div>
    );
};
Action.displayName = 'ProductCard.Action';

// i18n key + English fallback per kind. Kept out of `ProductCardActionState` so analytics can't wire
// to the locale-specific label.
const ACTION_KIND_COPY: Record<ProductCardActionKind, { key: string; fallback: string }> = {
    'add-to-bag': { key: 'addToBag', fallback: 'Add to Bag' },
    'notify-me': { key: 'notifyMe', fallback: 'Notify Me' },
    'out-of-stock': { key: 'outOfStock', fallback: 'Out of Stock' },
    'select-shade': { key: 'selectShade', fallback: 'Select Shade' },
    'select-size': { key: 'selectSize', fallback: 'Select Size' },
};

const ActionAutoButton = ({
    product,
    kind,
    onActionClick,
}: {
    product: NonNullable<ReturnType<typeof useProduct>>;
    kind?: ProductCardActionKind;
    onActionClick?: ProductCardActionClickHandler;
}) => {
    const { t } = useTranslation('productCard');
    const action = getProductCardActionState(product, { kind });
    const copy = ACTION_KIND_COPY[action.kind];
    const button = (
        <Button
            type="button"
            variant={action.variant}
            size="sm"
            disabled={action.disabled}
            onClick={() => onActionClick?.(action, product)}
            data-slot="product-card-action-auto"
            className="w-full md:h-[38px] md:py-2">
            {t(copy.key, { defaultValue: copy.fallback })}
        </Button>
    );
    return action.kind === 'notify-me' ? <NotifyMeTrigger productId={product.id}>{button}</NotifyMeTrigger> : button;
};

interface AddToBagButtonProps extends ComponentPropsWithRef<'div'> {
    kind?: ProductCardActionKind;
    onActionClick?: ProductCardActionClickHandler;
}

export const AddToBagButton = ({ children, kind, onActionClick, className, ref, ...props }: AddToBagButtonProps) => {
    const product = useProduct();
    const hasOverride = children !== undefined && children !== null && children !== false;
    if (!hasOverride && !product) return null;

    return (
        <div
            ref={ref}
            data-slot="product-card-add-to-bag-button"
            className={cn(
                'absolute right-2 bottom-2 z-10 md:hidden',
                // Desktop landscape: reveal on hover/focus-within. Both conditions sit on the SAME
                // ancestor, so the arbitrary group variant is required — stacking group-data-*/group-hover
                // would compile to two nested ancestors. Mobile stays visible (no hover).
                'md:group-[[data-layout=landscape]:hover]/product-card:block',
                'md:group-[[data-layout=landscape]:focus-within]/product-card:block',
                className
            )}
            {...props}>
            {hasOverride ? (
                children
            ) : product ? (
                <AddToBagAutoButton product={product} kind={kind} onActionClick={onActionClick} />
            ) : /* v8 ignore next -- unreachable: early return fires when !hasOverride && !product */ null}
        </div>
    );
};
AddToBagButton.displayName = 'ProductCard.AddToBagButton';

const AddToBagAutoButton = ({
    product,
    kind,
    onActionClick,
}: {
    product: NonNullable<ReturnType<typeof useProduct>>;
    kind?: ProductCardActionKind;
    onActionClick?: ProductCardActionClickHandler;
}) => {
    const { t } = useTranslation('productCard');
    const action = getProductCardActionState(product, { kind });
    const copy = ACTION_KIND_COPY[action.kind];
    const button = (
        <Button
            type="button"
            variant="primary"
            size="icon"
            disabled={action.disabled}
            onClick={() => onActionClick?.(action, product)}
            data-slot="product-card-add-to-bag-button-auto"
            className="size-10 shrink-0"
            aria-label={t(copy.key, { defaultValue: copy.fallback })}>
            <AddToBagIcon className="size-5" aria-hidden />
        </Button>
    );
    return action.kind === 'notify-me' ? <NotifyMeTrigger productId={product.id}>{button}</NotifyMeTrigger> : button;
};

// MatchScore — MatchesProgress is 'use client', so this slot must live here too.

interface MatchScoreProps extends ComponentPropsWithRef<'div'> {
    value: number;
    label?: string;
}

export const MatchScore = ({ value, label, className, ref, ...props }: MatchScoreProps) => {
    const { t } = useTranslation('productCard');
    return (
        <MatchesProgress.Root ref={ref} data-slot="product-card-match-score" className={className} {...props}>
            <MatchesProgress.Progress value={value} />
            <MatchesProgress.Text>
                {label ?? t('matchScore', { value, defaultValue: '{{value}}% Match' })}
            </MatchesProgress.Text>
        </MatchesProgress.Root>
    );
};
MatchScore.displayName = 'ProductCard.MatchScore';

// Rating — StarRating is 'use client', so this slot must live here too.

type RatingCustomFields = { c_bvAverageRating?: number; c_bvReviewCount?: number };

interface RatingProps extends ComponentPropsWithRef<'div'> {
    /** Override the numeric star rating (0–5). When omitted, reads `c_bvAverageRating` from context. */
    rating?: number;
    /** Override the review count shown in parentheses. When omitted, reads `c_bvReviewCount` from context. */
    count?: number;
}

export const Rating = ({ rating, count, className, ref, ...props }: RatingProps) => {
    const product = useProduct();
    const custom = product as (typeof product & RatingCustomFields) | null;
    const resolvedRating = rating ?? custom?.c_bvAverageRating ?? null;
    if (resolvedRating === null) return null;
    const resolvedCount = count ?? custom?.c_bvReviewCount;
    return (
        <StarRating.Root
            ref={ref}
            data-slot="product-card-rating"
            className={cn('justify-center', className)}
            {...props}>
            <StarRating.Stars rating={resolvedRating} size="sm" starGap="tight" />
            {resolvedCount !== undefined && <StarRating.Count value={resolvedCount} />}
        </StarRating.Root>
    );
};
Rating.displayName = 'ProductCard.Rating';

// Attribute — useState for internal selection keeps this client-only.

interface AttributeProps extends Omit<ComponentPropsWithRef<'div'>, 'onChange'> {
    value?: string;
    onChange?: (id: string) => void;
    /** Fixed cap for visible swatches; when omitted, the row measures its width and fits one line. */
    maxVisible?: number;
    /** Called when the overflow (“+”) control is activated. */
    onOverflowClick?: () => void;
}

export const Attribute = ({
    value,
    onChange,
    maxVisible,
    onOverflowClick,
    className,
    ref,
    ...props
}: AttributeProps) => {
    const product = useProduct();
    const [internal, setInternal] = useState<string | undefined>(undefined);
    const attribute = product ? getProductCardAttribute(product) : null;
    const { containerRef, visibleCount: measuredVisible } = useVisibleSwatchCount(attribute?.values.length ?? 0);

    if (!attribute) return null;

    // Fallback chain: controlled value → user's click → variant's own pre-selection.
    const selected = value ?? internal ?? attribute.initiallySelected;
    const handleSelect = (id: string) => {
        if (value === undefined) setInternal(id);
        onChange?.(id);
    };

    if (attribute.kind === 'colour') {
        const resolvedVisibleCount =
            maxVisible !== undefined ? Math.min(maxVisible, attribute.values.length) : measuredVisible;
        const visible = attribute.values.slice(0, resolvedVisibleCount);
        const overflow = Math.max(0, attribute.values.length - resolvedVisibleCount);

        return (
            <div
                ref={(node) => {
                    containerRef.current = node;
                    if (typeof ref === 'function') ref(node);
                    else if (ref) ref.current = node;
                }}
                data-slot="product-card-attribute"
                data-attribute-kind="colour"
                className={cn(
                    'flex w-full flex-nowrap items-center justify-center gap-[6px] py-[3px]',
                    'group-data-[layout=landscape]/product-card:justify-start',
                    className
                )}
                {...props}>
                {visible.map((shade) => {
                    const isSelected = shade.id === selected;
                    return (
                        <Swatch
                            key={shade.id}
                            size="sm"
                            colour={shade.color}
                            checked={isSelected}
                            status={shade.isOutOfStock ? 'unavailable' : 'default'}
                            onCheckedChange={() => handleSelect(shade.id)}
                            aria-label={shade.label}
                            className="size-[18px] shrink-0"
                        />
                    );
                })}
                {overflow > 0 && (
                    <button
                        type="button"
                        data-slot="product-card-attribute-overflow"
                        aria-label={`${overflow} more shades`}
                        onClick={onOverflowClick}
                        className="inline-flex size-[18px] shrink-0 cursor-pointer items-center justify-center text-foreground transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                        <PlusSwatchIcon className="size-[18px]" aria-hidden />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div
            ref={ref}
            data-slot="product-card-attribute"
            data-attribute-kind="size"
            className={cn(
                'flex w-full flex-wrap items-center justify-center gap-1',
                'group-data-[layout=landscape]/product-card:justify-start',
                className
            )}
            {...props}>
            {attribute.values.map((size) => {
                const isSelected = size.id === selected;
                return (
                    <OptionChip
                        key={size.id}
                        isSelected={isSelected}
                        isOutOfStock={size.isOutOfStock}
                        aria-label={size.label}
                        onClick={() => handleSelect(size.id)}>
                        {size.label}
                    </OptionChip>
                );
            })}
        </div>
    );
};
Attribute.displayName = 'ProductCard.Attribute';
